import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ScanText } from 'lucide-react';
import { cn } from '../lib/utils';
import { loadToken } from '../api';

const navLinks = [
  { label: 'Features',      href: '#features'     },
  { label: 'How it Works',  href: '#how-it-works'  },
  { label: 'Credits',       href: '#credits'       },
  { label: 'Pricing',       href: '#pricing'       },
  { label: 'API Docs',      href: 'https://ocr.tyosolution.com/docs' },
];

export default function Navbar({ onGetStarted, onSignIn }) {
  const [scrolled,    setScrolled]    = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [isLoggedIn,  setIsLoggedIn]  = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoggedIn(!!loadToken());
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goto = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      className={cn(
        'fixed top-0 inset-x-0 z-50 transition-all duration-300',
        scrolled
          ? 'bg-card/95 backdrop-blur-md border-b border-border shadow-card'
          : 'bg-transparent'
      )}
    >
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='flex items-center justify-between h-16'>
          {/* Logo */}
          <div className='flex items-center gap-2 cursor-pointer' onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className={cn(
              'w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300',
              scrolled ? 'bg-primary' : 'bg-primary-foreground/15'
            )}>
              <ScanText className={cn(
                'w-4.5 h-4.5',
                scrolled ? 'text-primary-foreground' : 'text-primary-foreground'
              )} strokeWidth={2.2} />
            </div>
            <span className={cn(
              'font-heading font-bold text-lg leading-none transition-colors duration-300',
              scrolled ? 'text-foreground' : 'text-primary-foreground'
            )}>
              Invoice<span className={scrolled ? 'text-primary' : 'text-accent-light'}>Reader</span>
            </span>
          </div>

          {/* Desktop nav */}
          <nav className='hidden lg:flex items-center gap-6'>
            {navLinks.map((l) => (
              l.href.startsWith('http') ? (
                <a
                  key={l.label}
                  href={l.href}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={cn(
                    'text-sm font-medium relative group transition-colors duration-200',
                    scrolled ? 'text-muted-foreground hover:text-foreground' : 'text-primary-foreground/80 hover:text-primary-foreground'
                  )}
                >
                  {l.label}
                  <span className='absolute -bottom-0.5 left-0 w-0 h-0.5 bg-accent rounded-full transition-all duration-300 group-hover:w-full' />
                </a>
              ) : (
                <button
                  key={l.label}
                  onClick={() => goto(l.href)}
                  className={cn(
                    'text-sm font-medium relative group transition-colors duration-200',
                    scrolled ? 'text-muted-foreground hover:text-foreground' : 'text-primary-foreground/80 hover:text-primary-foreground'
                  )}
                >
                  {l.label}
                  <span className='absolute -bottom-0.5 left-0 w-0 h-0.5 bg-accent rounded-full transition-all duration-300 group-hover:w-full' />
                </button>
              )
            ))}
          </nav>

          {/* CTA buttons */}
          <div className='hidden lg:flex items-center gap-3'>
            {isLoggedIn ? (
              <Button
                variant={scrolled ? 'default' : 'accent'}
                size='sm'
                onClick={() => navigate('/dashboard')}
                className='shadow-accent'
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant={scrolled ? 'ghost' : 'hero'}
                  size='sm'
                  onClick={onSignIn}
                >
                  Sign In
                </Button>
                <Button
                  variant={scrolled ? 'default' : 'accent'}
                  size='sm'
                  onClick={onGetStarted}
                  className='shadow-accent'
                >
                  Get Started Free
                </Button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className={cn(
              'lg:hidden p-2 rounded-md transition-colors duration-150',
              scrolled ? 'text-foreground hover:bg-muted' : 'text-primary-foreground hover:bg-primary-foreground/10'
            )}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label='Toggle menu'
          >
            {mobileOpen ? <X className='w-5 h-5' /> : <Menu className='w-5 h-5' />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className='lg:hidden bg-card border-t border-border'>
          <div className='px-4 py-4 space-y-1'>
            {navLinks.map((l) => (
              <button
                key={l.label}
                onClick={() => goto(l.href)}
                className='block w-full text-left px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface rounded-md transition-colors duration-150'
              >
                {l.label}
              </button>
            ))}
            <div className='pt-3 flex flex-col gap-2'>
              <Button variant='outline' size='sm' className='w-full' onClick={onSignIn}>Sign In</Button>
              <Button variant='default' size='sm' className='w-full' onClick={onGetStarted}>Get Started Free</Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
